///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Placemarks sync orchestrator, a clone of savedViewsSync. Local-first,
/// last-write-wins (whole-placemark granularity):
///
/// 1. GET the server delta since lastSyncedAt (tombstones included)
/// 2. Decide which local placemarks/deletions are newer and must be pushed
/// 3. Merge the server delta into the local slice (LWW per placemark)
/// 4. Push local winners (PUT) and tombstones (DELETE)
/// 5. Advance lastSyncedAt to server_time only if every push succeeded,
///    so failed pushes are retried on the next sync
///
/// On first sign-in lastSyncedAt is null, so all anonymous local
/// placemarks are pushed up automatically (ids are UUIDs, no merge
/// conflicts).
///

import type { PlacemarkRecord } from "@shared-types"
import { placemarksApi, placemarkToPutRequest } from "./placemarksApi"
import { markPlacemarksSynced, mergeServerPlacemarks, removePlacemarkTombstones, type Placemark } from "./placemarksSlice"
import { appStore } from "./store"

let syncInFlight = false

/** Convert a server record to the client Placemark shape */
function recordToPlacemark(record: PlacemarkRecord): Placemark {
	return {
		id: record.id,
		name: record.name,
		locations: record.locations,
		anchorId: record.anchor_id ?? undefined,
		color: record.color,
		note: record.note ?? undefined,
		tags: record.tags,
		createdAt: record.created_at,
		updatedAt: record.updated_at,
	}
}

/**
 * Run a full delta sync. Safe to call at any time: no-ops when signed
 * out or when a sync is already running. Failures leave the local state
 * intact and are retried on the next trigger (sign-in, reconnect, next
 * app start).
 */
export async function syncPlacemarks(): Promise<void> {
	if (syncInFlight) return
	if (!appStore.getState().user.userProfile) return

	syncInFlight = true
	try {
		const since = appStore.getState().placemarks.lastSyncedAt

		// 1. Fetch the server delta
		const subscription = appStore.dispatch(
			placemarksApi.endpoints.getPlacemarksDelta.initiate({ since }, { forceRefetch: true })
		)
		let delta
		try {
			delta = await subscription.unwrap()
		} finally {
			subscription.unsubscribe()
		}

		// 2. Decide pushes from the pre-merge snapshot
		const local = appStore.getState().placemarks
		const serverById = new Map(delta.placemarks.map(record => [record.id, record]))

		const pushes = Object.values(local.placemarks).filter(placemark => {
			const server = serverById.get(placemark.id)
			// Server copy (or deletion) is same-or-newer: server wins
			if (server && server.updated_at >= placemark.updatedAt) return false
			// Changed since the last sync, or never synced, or newer than
			// the server copy: push it up
			return since === null || placemark.updatedAt > since || server !== undefined
		})
		const deletions = Object.keys(local.deleted).filter(id => {
			const server = serverById.get(id)
			// Server re-created the placemark after our deletion: server wins
			return !(server && !server.deleted_at && server.updated_at >= local.deleted[id])
		})

		// 3. Merge the server delta locally (LWW per placemark)
		appStore.dispatch(mergeServerPlacemarks({
			placemarks: delta.placemarks.filter(record => !record.deleted_at).map(recordToPlacemark),
			tombstones: delta.placemarks
				.filter(record => record.deleted_at)
				.map(record => ({ id: record.id, deletedAt: record.deleted_at! })),
		}))

		// 4. Push local winners
		let allPushesSucceeded = true

		for (const placemark of pushes) {
			try {
				await appStore.dispatch(
					placemarksApi.endpoints.putPlacemark.initiate({ id: placemark.id, body: placemarkToPutRequest(placemark) })
				).unwrap()
			} catch (error) {
				console.warn(`[placemarksSync] Failed to push placemark ${placemark.id}:`, error)
				allPushesSucceeded = false
			}
		}

		const deletedOnServer: string[] = []
		for (const id of deletions) {
			try {
				await appStore.dispatch(placemarksApi.endpoints.deletePlacemark.initiate(id)).unwrap()
				deletedOnServer.push(id)
			} catch (error) {
				console.warn(`[placemarksSync] Failed to push deletion ${id}:`, error)
				allPushesSucceeded = false
			}
		}
		if (deletedOnServer.length > 0) {
			appStore.dispatch(removePlacemarkTombstones(deletedOnServer))
		}

		// 5. Advance the cursor only when everything reached the server
		if (allPushesSucceeded) {
			appStore.dispatch(markPlacemarksSynced(delta.server_time))
		}
	} catch (error) {
		console.warn("[placemarksSync] Sync failed (will retry on next trigger):", error)
	} finally {
		syncInFlight = false
	}
}
